import { Controller } from "@hotwired/stimulus"

// Arrow keys and native dragging share the same persisted order. Only one request
// can be in flight; a failed save restores the last acknowledged order.
export default class extends Controller {
  static values = { url: String, param: { type: String, default: "ids" }, help: String, saved: String, error: String }

  connect() {
    this.listeners = new AbortController()
    this.savedOrder = this.items
    this.status = document.createElement("p")
    this.status.setAttribute("role", "status")
    this.status.className = "text-sm text-secondary"
    this.element.after(this.status)
    this.items.forEach((item) => this.wire(item))
  }

  disconnect() {
    this.listeners.abort()
    this.status.remove()
  }

  get items() {
    return Array.from(this.element.querySelectorAll(":scope > [data-sortable-id]"))
  }

  wire(item) {
    const handle = item.querySelector("[data-sortable-target='handle']") || item
    handle.tabIndex = 0
    handle.setAttribute("role", "button")
    handle.setAttribute("aria-label", this.helpValue)
    const on = (node, event, callback) => node.addEventListener(event, callback, { signal: this.listeners.signal })
    on(handle, "keydown", (event) => {
      if (!["ArrowUp", "ArrowDown"].includes(event.key)) return
      event.preventDefault()
      event.stopPropagation()
      if (this.saving) return
      const neighbor = this.items[this.items.indexOf(item) + (event.key === "ArrowUp" ? -1 : 1)]
      if (!neighbor) return
      if (event.key === "ArrowUp") neighbor.before(item)
      else neighbor.after(item)
      handle.focus()
      this.persist(handle)
    })
    on(handle, "mousedown", () => { item.draggable = !this.saving })
    on(handle, "mouseup", () => { item.draggable = false })
    on(item, "dragstart", (event) => {
      event.stopPropagation()
      if (this.saving) { event.preventDefault(); return }
      this.dragging = item
      item.classList.add("opacity-50")
      event.dataTransfer.effectAllowed = "move"
    })
    on(item, "dragover", (event) => {
      event.preventDefault()
      event.stopPropagation()
      if (!this.dragging || this.dragging === item || this.saving) return
      if (this.items.indexOf(this.dragging) < this.items.indexOf(item)) item.after(this.dragging)
      else item.before(this.dragging)
    })
    on(item, "drop", (event) => { event.preventDefault(); event.stopPropagation() })
    on(item, "dragend", (event) => {
      event.stopPropagation()
      item.draggable = false
      item.classList.remove("opacity-50")
      this.dragging = null
      this.persist(handle)
    })
  }

  async persist(handle) {
    if (this.saving) return
    this.saving = true
    this.element.setAttribute("aria-busy", "true")
    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH", redirect: "error", signal: AbortSignal.timeout(15000),
        headers: { "Content-Type": "application/json", "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content },
        body: JSON.stringify({ [this.paramValue]: this.items.map((item) => item.dataset.sortableId) }),
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      this.savedOrder = this.items
      this.status.textContent = this.savedValue
    } catch {
      this.savedOrder.forEach((item) => this.element.append(item))
      this.status.textContent = this.errorValue
      if (this.element.isConnected) handle.focus()
    } finally {
      this.saving = false
      this.element.removeAttribute("aria-busy")
    }
  }
}
